import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Student, Teacher, SchoolSettings, ScoutAdministrationData, ScoutRegu, ScoutMember, ScoutRole } from '../../types.ts';
import * as XLSX from 'xlsx';
import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase.ts';
import {
  Compass,
  Plus,
  Search,
  Trash2,
  Edit3,
  Check,
  X,
  Printer,
  Download,
  Sparkles,
  Users,
  Shield,
  Crown,
  UserCheck,
  Award,
  BookOpen,
  Info,
  ChevronDown,
  ArrowUpDown,
  Flame,
  Save,
  CheckCircle2,
  RefreshCw,
  FolderPlus,
  HelpCircle,
  Copy,
  Layers,
  LayoutGrid,
  Table as TableIcon,
  UserPlus,
  SlidersHorizontal,
  ChevronRight,
  Filter,
  Cloud,
  CloudCheck,
  CloudOff,
  Database,
  UploadCloud
} from 'lucide-react';

interface PramukaAdministrationManagerProps {
  homeroomClass: string;
  students: Student[];
  allStudents?: Student[];
  classes?: string[];
  onSelectHomeroomClass?: (cls: string) => void;
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  appTheme?: string;
  isTopLevelView?: boolean;
}

const PRESET_REGU_PUTRA = [
  'Regu Garuda',
  'Regu Rajawali',
  'Regu Elang',
  'Regu Harimau',
  'Regu Singa',
  'Regu Serigala',
  'Regu Banteng',
  'Regu Kancil',
  'Regu Kobra',
  'Regu Scorpio'
];

const PRESET_REGU_PUTRI = [
  'Regu Melati',
  'Regu Mawar',
  'Regu Anggrek',
  'Regu Dahlia',
  'Regu Teratai',
  'Regu Sakura',
  'Regu Cempaka',
  'Regu Flamboyan',
  'Regu Kenanga',
  'Regu Edelweis'
];

export const PramukaAdministrationManager: React.FC<PramukaAdministrationManagerProps> = ({
  homeroomClass,
  students = [],
  allStudents = [],
  classes = [],
  onSelectHomeroomClass,
  activeUser,
  schoolSettings,
  isTopLevelView = false,
}) => {
  const currentClass = homeroomClass || '9A';
  const storageKey = `pramuka_data_${currentClass || 'general'}`;
  const firestoreDocId = useMemo(() => `scout_${(currentClass || 'general').replace(/[^a-zA-Z0-9]/g, '_')}`, [currentClass]);

  // Firestore sync status states
  const [cloudSyncStatus, setCloudSyncStatus] = useState<'synced' | 'syncing' | 'offline' | 'error'>('syncing');
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  // Helper to sanitize payload for Firestore (remove undefined values)
  const cleanForFirestore = useCallback((obj: any): any => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(cleanForFirestore);
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key of Object.keys(obj)) {
        if (obj[key] !== undefined) {
          cleaned[key] = cleanForFirestore(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  }, []);

  // Default initial scout data
  const defaultData: ScoutAdministrationData = useMemo(() => {
    const schoolName = schoolSettings?.schoolName || 'SMP Negeri 1 Kawali';
    return {
      id: firestoreDocId,
      homeroomClass: currentClass,
      gugusDepan: `01.001 - 01.002 Pangkalan ${schoolName}`,
      namaPasukan: `Pasukan Penggalang Kelas ${currentClass}`,
      pembinaPutra: activeUser?.name || 'Pembina Pramuka Putra',
      pembinaPutri: 'Pembina Pramuka Putri',
      reguList: [
        {
          id: `regu-${Date.now()}-1`,
          name: 'Regu Garuda',
          category: 'Putra',
          motto: 'Tangkas, Cerdas, Berani',
          members: []
        },
        {
          id: `regu-${Date.now()}-2`,
          name: 'Regu Melati',
          category: 'Putri',
          motto: 'Harum, Anggun, Berprestasi',
          members: []
        }
      ],
      academicYear: schoolSettings?.academicYear || '2025/2026',
      semester: schoolSettings?.semester || 'Ganjil',
      updatedAt: new Date().toISOString()
    };
  }, [currentClass, schoolSettings, activeUser, firestoreDocId]);

  // Load from localStorage or use default as initial state
  const [scoutData, setScoutData] = useState<ScoutAdministrationData>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.reguList) return { ...parsed, id: firestoreDocId };
      }
    } catch {
      // ignore JSON parse errors
    }
    return defaultData;
  });

  // REALTIME FIRESTORE LISTENER
  useEffect(() => {
    if (!db) {
      setCloudSyncStatus('offline');
      return;
    }

    setCloudSyncStatus('syncing');
    const docRef = doc(db, 'scoutAdministrations', firestoreDocId);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const cloudData = snapshot.data() as ScoutAdministrationData;
          if (cloudData && Array.isArray(cloudData.reguList)) {
            setScoutData(cloudData);
            try {
              localStorage.setItem(storageKey, JSON.stringify(cloudData));
            } catch (e) {
              console.warn('Could not cache scout data to localStorage', e);
            }
            setCloudSyncStatus('synced');
            setLastSyncTime(new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
            return;
          }
        }

        // If doc does not exist in Firestore yet, initialize it with current local or default data
        const initialToSave = { ...scoutData, id: firestoreDocId, homeroomClass: currentClass, updatedAt: new Date().toISOString() };
        setDoc(docRef, cleanForFirestore(initialToSave), { merge: true })
          .then(() => {
            setCloudSyncStatus('synced');
            setLastSyncTime(new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
          })
          .catch((err) => {
            console.error('Initial scout Firestore sync error:', err);
            setCloudSyncStatus('error');
          });
      },
      (error) => {
        console.error('Firestore listener error on scoutAdministrations:', error);
        setCloudSyncStatus('error');
      }
    );

    return () => unsubscribe();
  }, [firestoreDocId, currentClass, storageKey, cleanForFirestore]);

  // Auto-save notification
  const [saveToast, setSaveToast] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'SEMUA' | 'Putra' | 'Putri'>('SEMUA');
  const [roleFilter, setRoleFilter] = useState<'SEMUA' | ScoutRole>('SEMUA');
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'structure'>('grid');

  // Save changes to Firebase Firestore + localStorage helper
  const saveScoutData = async (newData: ScoutAdministrationData) => {
    const updated: ScoutAdministrationData = {
      ...newData,
      id: firestoreDocId,
      homeroomClass: currentClass,
      updatedAt: new Date().toISOString()
    };

    setScoutData(updated);

    // 1. Save to localStorage backup
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2000);
    } catch (e) {
      console.error('Failed to save scout data to localStorage', e);
    }

    // 2. Save directly to Firebase Firestore
    if (db) {
      try {
        setCloudSyncStatus('syncing');
        const docRef = doc(db, 'scoutAdministrations', firestoreDocId);
        await setDoc(docRef, cleanForFirestore(updated), { merge: true });
        setCloudSyncStatus('synced');
        setLastSyncTime(new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      } catch (err) {
        console.error('Firebase save error on scoutAdministrations:', err);
        setCloudSyncStatus('error');
      }
    }
  };

  // Manual Trigger Sync
  const handleManualSync = async () => {
    setIsManualSyncing(true);
    await saveScoutData(scoutData);
    setTimeout(() => {
      setIsManualSyncing(false);
    }, 800);
  };

  // Regu Modal State
  const [isReguModalOpen, setIsReguModalOpen] = useState(false);
  const [editingRegu, setEditingRegu] = useState<ScoutRegu | null>(null);
  const [reguName, setReguName] = useState('');
  const [reguCategory, setReguCategory] = useState<'Putra' | 'Putri'>('Putra');
  const [reguMotto, setReguMotto] = useState('');

  // Member Modal State
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [targetReguId, setTargetReguId] = useState<string>('');
  const [editingMember, setEditingMember] = useState<ScoutMember | null>(null);
  const [memberRole, setMemberRole] = useState<ScoutRole>('Anggota');
  const [memberName, setMemberName] = useState('');
  const [memberClass, setMemberClass] = useState(currentClass);
  const [memberNta, setMemberNta] = useState('');
  const [memberStudentId, setMemberStudentId] = useState('');
  const [memberNotes, setMemberNotes] = useState('');

  // Auto-distribute Modal State
  const [isAutoDistributeModalOpen, setIsAutoDistributeModalOpen] = useState(false);
  const [membersPerRegu, setMembersPerRegu] = useState<number>(8);

  // Available students list (prefer current class students, fallback to allStudents)
  const availableClassStudents = useMemo(() => {
    if (students && students.length > 0) return students;
    if (allStudents && allStudents.length > 0) {
      return allStudents.filter(s => s.class === currentClass);
    }
    return [];
  }, [students, allStudents, currentClass]);

  // Open Regu Modal for adding new
  const handleOpenAddRegu = () => {
    setEditingRegu(null);
    setReguName('');
    setReguCategory('Putra');
    setReguMotto('');
    setIsReguModalOpen(true);
  };

  // Open Regu Modal for editing
  const handleOpenEditRegu = (regu: ScoutRegu) => {
    setEditingRegu(regu);
    setReguName(regu.name);
    setReguCategory(regu.category);
    setReguMotto(regu.motto || '');
    setIsReguModalOpen(true);
  };

  // Save Regu (Add or Edit)
  const handleSaveRegu = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reguName.trim()) return;

    let updatedReguList: ScoutRegu[];
    if (editingRegu) {
      updatedReguList = scoutData.reguList.map(r => {
        if (r.id === editingRegu.id) {
          return {
            ...r,
            name: reguName.trim(),
            category: reguCategory,
            motto: reguMotto.trim()
          };
        }
        return r;
      });
    } else {
      const newRegu: ScoutRegu = {
        id: `regu-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        name: reguName.trim(),
        category: reguCategory,
        motto: reguMotto.trim(),
        members: []
      };
      updatedReguList = [...scoutData.reguList, newRegu];
    }

    saveScoutData({
      ...scoutData,
      reguList: updatedReguList
    });
    setIsReguModalOpen(false);
  };

  // Delete Regu
  const handleDeleteRegu = (reguId: string, rName: string) => {
    if (window.confirm(`Hapus ${rName} beserta seluruh anggota di dalamnya?`)) {
      const updatedReguList = scoutData.reguList.filter(r => r.id !== reguId);
      saveScoutData({
        ...scoutData,
        reguList: updatedReguList
      });
    }
  };

  // Open Member Modal for adding new
  const handleOpenAddMember = (reguId: string) => {
    const regu = scoutData.reguList.find(r => r.id === reguId);
    setTargetReguId(reguId);
    setEditingMember(null);
    setMemberRole('Anggota');
    setMemberName('');
    setMemberClass(currentClass);
    
    // Generate sample NTA based on regu/school code
    const existingCount = scoutData.reguList.reduce((acc, r) => acc + r.members.length, 0);
    const ntaNum = String(existingCount + 1).padStart(3, '0');
    setMemberNta(`10.07.01.${ntaNum}`);
    setMemberStudentId('');
    setMemberNotes('');
    setIsMemberModalOpen(true);
  };

  // Open Member Modal for editing
  const handleOpenEditMember = (reguId: string, member: ScoutMember) => {
    setTargetReguId(reguId);
    setEditingMember(member);
    setMemberRole(member.role);
    setMemberName(member.name);
    setMemberClass(member.class);
    setMemberNta(member.nta || '');
    setMemberStudentId(member.studentId || '');
    setMemberNotes(member.notes || '');
    setIsMemberModalOpen(true);
  };

  // Handle student quick select from class
  const handleSelectStudentForMember = (studentId: string) => {
    if (!studentId) return;
    const pool = availableClassStudents.length > 0 ? availableClassStudents : allStudents;
    const student = pool.find(s => s.id === studentId);
    if (student) {
      setMemberStudentId(student.id);
      setMemberName(student.name);
      setMemberClass(student.class || currentClass);
      if (student.nisn || student.nis) {
        setMemberNta(`NTA-${student.nisn || student.nis}`);
      }
    }
  };

  // Save Member (Add or Edit)
  const handleSaveMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberName.trim() || !targetReguId) return;

    const updatedReguList = scoutData.reguList.map(r => {
      if (r.id !== targetReguId) return r;

      let newMembers: ScoutMember[];
      if (editingMember) {
        newMembers = r.members.map(m => {
          if (m.id === editingMember.id) {
            return {
              ...m,
              name: memberName.trim(),
              class: memberClass.trim() || currentClass,
              nta: memberNta.trim(),
              role: memberRole,
              studentId: memberStudentId || undefined,
              notes: memberNotes.trim() || undefined
            };
          }
          return m;
        });
      } else {
        const newMember: ScoutMember = {
          id: `member-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          name: memberName.trim(),
          class: memberClass.trim() || currentClass,
          nta: memberNta.trim(),
          role: memberRole,
          studentId: memberStudentId || undefined,
          notes: memberNotes.trim() || undefined
        };
        newMembers = [...r.members, newMember];
      }

      return {
        ...r,
        members: newMembers
      };
    });

    saveScoutData({
      ...scoutData,
      reguList: updatedReguList
    });
    setIsMemberModalOpen(false);
  };

  // Delete Member
  const handleDeleteMember = (reguId: string, memberId: string, mName: string) => {
    if (window.confirm(`Hapus anggota ${mName} dari regu ini?`)) {
      const updatedReguList = scoutData.reguList.map(r => {
        if (r.id !== reguId) return r;
        return {
          ...r,
          members: r.members.filter(m => m.id !== memberId)
        };
      });
      saveScoutData({
        ...scoutData,
        reguList: updatedReguList
      });
    }
  };

  // Change member role directly
  const handleQuickChangeRole = (reguId: string, memberId: string, newRole: ScoutRole) => {
    const updatedReguList = scoutData.reguList.map(r => {
      if (r.id !== reguId) return r;
      return {
        ...r,
        members: r.members.map(m => (m.id === memberId ? { ...m, role: newRole } : m))
      };
    });
    saveScoutData({
      ...scoutData,
      reguList: updatedReguList
    });
  };

  // Auto-distribute students of current class into scout regu
  const handleExecuteAutoDistribute = () => {
    if (availableClassStudents.length === 0) {
      alert('Tidak ada data siswa di kelas ini untuk dibagi ke dalam regu.');
      return;
    }

    if (!window.confirm(`Otomatisasi akan membagi ${availableClassStudents.length} siswa kelas ${currentClass} ke dalam Regu Putra dan Regu Putri serta menunjuk Pinru, Wapinru, dan Anggota. Lanjutkan?`)) {
      return;
    }

    const putraStudents = availableClassStudents.filter(s => (s.gender || '').toLowerCase().startsWith('l') || (s.gender || '').toLowerCase().includes('putra') || (s.gender || '').toLowerCase().includes('laki'));
    const putriStudents = availableClassStudents.filter(s => (s.gender || '').toLowerCase().startsWith('p') || (s.gender || '').toLowerCase().includes('putri') || (s.gender || '').toLowerCase().includes('perempuan'));

    // Remaining unclassified students (if gender not filled, split evenly)
    const otherStudents = availableClassStudents.filter(s => !putraStudents.includes(s) && !putriStudents.includes(s));
    otherStudents.forEach((s, idx) => {
      if (idx % 2 === 0) putraStudents.push(s);
      else putriStudents.push(s);
    });

    const newReguList: ScoutRegu[] = [];
    const size = Math.max(4, Math.min(12, membersPerRegu || 8));

    // Helper to chunk array
    const chunk = <T,>(arr: T[], chunkSize: number): T[][] => {
      const res: T[][] = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
        res.push(arr.slice(i, i + chunkSize));
      }
      return res;
    };

    const putraChunks = chunk(putraStudents, size);
    const putriChunks = chunk(putriStudents, size);

    let globalNtaCounter = 1;

    // Distribute Putra
    putraChunks.forEach((group, gIdx) => {
      const reguNameChosen = PRESET_REGU_PUTRA[gIdx % PRESET_REGU_PUTRA.length] || `Regu Putra ${gIdx + 1}`;
      const reguId = `regu-auto-pa-${Date.now()}-${gIdx}`;
      const members: ScoutMember[] = group.map((st, mIdx) => {
        let role: ScoutRole = 'Anggota';
        if (gIdx === 0 && mIdx === 0) {
          role = 'Pratama & Pinru'; // Pratama merangkap Pinru Regu Utama
        } else if (mIdx === 0) {
          role = 'Pinru';
        } else if (mIdx === 1) {
          role = 'Wapinru';
        }

        const nta = st.nisn ? `NTA-${st.nisn}` : `10.07.01.${String(globalNtaCounter++).padStart(3, '0')}`;

        const getNotes = (r: ScoutRole): string | undefined => {
          if (r === 'Pratama & Pinru') return 'Pratama & Pinru Putra';
          if (r === 'Pratama') return 'Pratama Putra';
          if (r === 'Pinru') return 'Pemimpin Regu';
          if (r === 'Wapinru') return 'Wakil Pinru';
          return undefined;
        };

        return {
          id: `member-auto-${st.id || Date.now()}-${mIdx}`,
          studentId: st.id,
          name: st.name,
          class: st.class || currentClass,
          nta,
          role,
          notes: getNotes(role)
        };
      });

      newReguList.push({
        id: reguId,
        name: reguNameChosen,
        category: 'Putra',
        motto: 'Tangkas, Cerdas, Berani',
        members
      });
    });

    // Distribute Putri
    putriChunks.forEach((group, gIdx) => {
      const reguNameChosen = PRESET_REGU_PUTRI[gIdx % PRESET_REGU_PUTRI.length] || `Regu Putri ${gIdx + 1}`;
      const reguId = `regu-auto-pi-${Date.now()}-${gIdx}`;
      const members: ScoutMember[] = group.map((st, mIdx) => {
        let role: ScoutRole = 'Anggota';
        if (gIdx === 0 && mIdx === 0 && !newReguList.some(r => r.members.some(m => (m.role === 'Pratama' || m.role === 'Pratama & Pinru') && r.category === 'Putri'))) {
          role = 'Pratama & Pinru'; // Pratama Putri merangkap Pinru
        } else if (mIdx === 0) {
          role = 'Pinru';
        } else if (mIdx === 1) {
          role = 'Wapinru';
        }

        const nta = st.nisn ? `NTA-${st.nisn}` : `10.07.01.${String(globalNtaCounter++).padStart(3, '0')}`;

        const getNotes = (r: ScoutRole): string | undefined => {
          if (r === 'Pratama & Pinru') return 'Pratama & Pinru Putri';
          if (r === 'Pratama') return 'Pratama Putri';
          if (r === 'Pinru') return 'Pemimpin Regu';
          if (r === 'Wapinru') return 'Wakil Pinru';
          return undefined;
        };

        return {
          id: `member-auto-${st.id || Date.now()}-${mIdx}`,
          studentId: st.id,
          name: st.name,
          class: st.class || currentClass,
          nta,
          role,
          notes: getNotes(role)
        };
      });

      newReguList.push({
        id: reguId,
        name: reguNameChosen,
        category: 'Putri',
        motto: 'Harum, Anggun, Berprestasi',
        members
      });
    });

    saveScoutData({
      ...scoutData,
      reguList: newReguList.length > 0 ? newReguList : scoutData.reguList
    });

    setIsAutoDistributeModalOpen(false);
  };

  // Flattened all members with regu info for searching/filtering/stats
  const allMembersWithRegu = useMemo(() => {
    const list: (ScoutMember & { reguId: string; reguName: string; reguCategory: 'Putra' | 'Putri' })[] = [];
    (scoutData.reguList || []).forEach(r => {
      (r.members || []).forEach(m => {
        list.push({
          ...m,
          reguId: r.id,
          reguName: r.name,
          reguCategory: r.category
        });
      });
    });
    return list;
  }, [scoutData]);

  // Filtered members list
  const filteredMembers = useMemo(() => {
    return allMembersWithRegu.filter(m => {
      const matchCat = categoryFilter === 'SEMUA' || m.reguCategory === categoryFilter;
      const matchRole =
        roleFilter === 'SEMUA' ||
        m.role === roleFilter ||
        (roleFilter === 'Pratama' && (m.role === 'Pratama' || m.role === 'Pratama & Pinru')) ||
        (roleFilter === 'Pinru' && (m.role === 'Pinru' || m.role === 'Pratama & Pinru'));
      const term = searchQuery.toLowerCase();
      const matchSearch =
        !term ||
        m.name.toLowerCase().includes(term) ||
        (m.nta && m.nta.toLowerCase().includes(term)) ||
        m.class.toLowerCase().includes(term) ||
        m.reguName.toLowerCase().includes(term);
      return matchCat && matchRole && matchSearch;
    });
  }, [allMembersWithRegu, categoryFilter, roleFilter, searchQuery]);

  // Statistics
  const stats = useMemo(() => {
    const totalRegu = scoutData.reguList.length;
    const totalPutraRegu = scoutData.reguList.filter(r => r.category === 'Putra').length;
    const totalPutriRegu = scoutData.reguList.filter(r => r.category === 'Putri').length;
    const totalMembers = allMembersWithRegu.length;
    const totalPratama = allMembersWithRegu.filter(m => m.role === 'Pratama' || m.role === 'Pratama & Pinru').length;
    const totalPinru = allMembersWithRegu.filter(m => m.role === 'Pinru' || m.role === 'Pratama & Pinru').length;
    const totalPratamaPinru = allMembersWithRegu.filter(m => m.role === 'Pratama & Pinru').length;
    const totalWapinru = allMembersWithRegu.filter(m => m.role === 'Wapinru').length;
    const totalAnggota = allMembersWithRegu.filter(m => m.role === 'Anggota').length;

    return {
      totalRegu,
      totalPutraRegu,
      totalPutriRegu,
      totalMembers,
      totalPratama,
      totalPinru,
      totalPratamaPinru,
      totalWapinru,
      totalAnggota
    };
  }, [scoutData, allMembersWithRegu]);

  // Export to Excel
  const handleExportExcel = () => {
    const rows: any[] = [
      ['ADMINISTRASI GERAKAN PRAMUKA - STRUKTUR PASUKAN & REGU PENGGALANG'],
      ['Pangkalan / Sekolah:', schoolSettings?.schoolName || 'SMP Negeri 1 Kawali'],
      ['Gugus Depan:', scoutData.gugusDepan],
      ['Nama Pasukan:', scoutData.namaPasukan],
      ['Kelas / Rombel:', `Kelas ${currentClass}`],
      ['Tahun Ajaran / Semester:', `${scoutData.academicYear || '2025/2026'} - Semester ${scoutData.semester || 'Ganjil'}`],
      ['Pembina Putra:', scoutData.pembinaPutra || '-'],
      ['Pembina Putri:', scoutData.pembinaPutri || '-'],
      [],
      ['No', 'Nama Regu', 'Kategori Regu', 'Nama Siswa', 'Kelas', 'NTA (No. Tanda Anggota)', 'Jabatan', 'Catatan / Keterangan']
    ];

    let counter = 1;
    scoutData.reguList.forEach(regu => {
      if (regu.members.length === 0) {
        rows.push([counter++, regu.name, regu.category === 'Putra' ? 'Penggalang Putra' : 'Penggalang Putri', '(Belum ada anggota)', '-', '-', '-', regu.motto || '-']);
      } else {
        regu.members.forEach(member => {
          rows.push([
            counter++,
            regu.name,
            regu.category === 'Putra' ? 'Penggalang Putra' : 'Penggalang Putri',
            member.name,
            member.class,
            member.nta || '-',
            member.role,
            member.notes || regu.motto || '-'
          ]);
        });
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Administrasi_Pramuka');
    XLSX.writeFile(workbook, `Administrasi_Pramuka_Kelas_${currentClass}_${Date.now()}.xlsx`);
  };

  // Print scout document
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const schoolName = schoolSettings?.schoolName || 'SMP NEGERI 1 KAWALI';
    const schoolAddress = schoolSettings?.address || 'Jalan Veteran No. 45 Kawali - Ciamis';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Administrasi Pasukan & Regu Pramuka - Kelas ${currentClass}</title>
        <style>
          @page { size: A4 portrait; margin: 15mm; }
          body { font-family: 'Times New Roman', serif; color: #000; margin: 0; padding: 0; font-size: 11pt; line-height: 1.3; }
          .header { text-align: center; border-bottom: 3px double #000; padding-bottom: 8px; margin-bottom: 15px; }
          .header h2 { margin: 0; font-size: 14pt; text-transform: uppercase; font-weight: bold; }
          .header h3 { margin: 2px 0; font-size: 12pt; text-transform: uppercase; }
          .header p { margin: 2px 0; font-size: 9.5pt; font-style: italic; }
          .title { text-align: center; margin: 15px 0; }
          .title h4 { margin: 0; font-size: 12pt; text-decoration: underline; text-transform: uppercase; font-weight: bold; }
          .meta-box { margin-bottom: 15px; width: 100%; border-collapse: collapse; font-size: 10pt; }
          .meta-box td { padding: 3px 6px; }
          table.data-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9.5pt; }
          table.data-table th, table.data-table td { border: 1px solid #000; padding: 5px 6px; }
          table.data-table th { background-color: #f0f0f0; text-align: center; font-weight: bold; }
          .text-center { text-align: center; }
          .badge-role { font-weight: bold; font-size: 9pt; }
          .signature-section { margin-top: 30px; width: 100%; page-break-inside: avoid; }
          .sig-box { width: 33%; text-align: center; font-size: 10pt; vertical-align: top; display: inline-block; }
          .sig-space { height: 60px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>GERAKAN PRAMUKA GUGUS DEPAN</h2>
          <h3>${scoutData.gugusDepan || '01.001 - 01.002'}</h3>
          <h3>PANGKALAN ${schoolName}</h3>
          <p>${schoolAddress}</p>
        </div>

        <div class="title">
          <h4>BUKU INDUK ADMINISTRASI PASUKAN & REGU PENGGALANG</h4>
          <div style="font-size: 10pt; font-weight: bold; margin-top: 3px;">${scoutData.namaPasukan || `Pasukan Kelas ${currentClass}`}</div>
        </div>

        <table class="meta-box">
          <tr>
            <td width="20%"><strong>Pangkalan / Kelas</strong></td>
            <td width="30%">: ${schoolName} / Kelas ${currentClass}</td>
            <td width="20%"><strong>Tahun Ajaran</strong></td>
            <td width="30%">: ${scoutData.academicYear || '2025/2026'} (${scoutData.semester || 'Ganjil'})</td>
          </tr>
          <tr>
            <td><strong>Pembina Putra</strong></td>
            <td>: ${scoutData.pembinaPutra || '-'}</td>
            <td><strong>Pembina Putri</strong></td>
            <td>: ${scoutData.pembinaPutri || '-'}</td>
          </tr>
          <tr>
            <td><strong>Total Regu</strong></td>
            <td>: ${stats.totalRegu} Regu (${stats.totalPutraRegu} Putra, ${stats.totalPutriRegu} Putri)</td>
            <td><strong>Total Anggota</strong></td>
            <td>: ${stats.totalMembers} Siswa Penggalang</td>
          </tr>
        </table>

        ${scoutData.reguList.map((regu, rIdx) => `
          <div style="margin-top: 15px; page-break-inside: avoid;">
            <div style="font-weight: bold; font-size: 10.5pt; margin-bottom: 4px; border-bottom: 1px solid #666; padding-bottom: 2px;">
              ${rIdx + 1}. ${regu.name} (Penggalang ${regu.category}) ${regu.motto ? `- <em>"${regu.motto}"</em>` : ''}
            </div>
            <table class="data-table">
              <thead>
                <tr>
                  <th width="5%">No</th>
                  <th width="35%">Nama Anggota</th>
                  <th width="12%">Kelas</th>
                  <th width="20%">Nomor Tanda Anggota (NTA)</th>
                  <th width="15%">Jabatan</th>
                  <th width="13%">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                ${regu.members.length === 0 ? `
                  <tr><td colspan="6" class="text-center" style="font-style: italic; color: #666;">Belum ada anggota terdaftar di regu ini</td></tr>
                ` : regu.members.map((m, mIdx) => `
                  <tr>
                    <td class="text-center">${mIdx + 1}</td>
                    <td><strong>${m.name}</strong></td>
                    <td class="text-center">${m.class}</td>
                    <td class="text-center">${m.nta || '-'}</td>
                    <td class="text-center badge-role">${m.role}</td>
                    <td>${m.notes || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `).join('')}

        <div class="signature-section">
          <table style="width: 100%; border: none;">
            <tr>
              <td class="sig-box">
                Mengetahui,<br>
                Ketua Mabigus (Kepala Sekolah)<br>
                <div class="sig-space"></div>
                <strong>${schoolSettings?.principalName || '...........................................'}</strong><br>
                NIP. ${schoolSettings?.principalNip || '...........................................'}
              </td>
              <td class="sig-box">
                Pembina Pramuka Putra<br>
                <div class="sig-space"></div>
                <strong>${scoutData.pembinaPutra || '...........................................'}</strong><br>
                NTA. .....................................
              </td>
              <td class="sig-box">
                Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br>
                Pembina Pramuka Putri<br>
                <div class="sig-space"></div>
                <strong>${scoutData.pembinaPutri || '...........................................'}</strong><br>
                NTA. .....................................
              </td>
            </tr>
          </table>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* TOAST NOTIFICATION */}
      {saveToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-amber-600 text-white font-bold rounded-2xl shadow-xl animate-bounce text-xs">
          <CheckCircle2 size={16} /> Data Administrasi Pasukan Berhasil Disimpan!
        </div>
      )}

      {/* TOP CLASS SELECTOR BAR (ALIGNED WITH DATABASE WALI KELAS & WALI ROMBEL) */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
            <Compass size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-black text-slate-800 dark:text-white text-base">
                Administrasi Pasukan & Regu Penggalang
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300">
                Kelas {currentClass}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kelola struktur Gugus Depan, Pasukan, Regu Putra & Putri, Dewan Pasukan (Pratama, Pinru, Wapinru, Anggota), serta NTA.
            </p>
          </div>
        </div>

        {/* CLASS SWITCHER & ACTIONS SELECTOR */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* FIREBASE SYNC BADGE */}
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
              cloudSyncStatus === 'synced'
                ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                : cloudSyncStatus === 'syncing'
                ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                : 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
            }`}
            title={`Status Firebase: ${cloudSyncStatus === 'synced' ? 'Tersinkronisasi Realtime' : cloudSyncStatus === 'syncing' ? 'Sedang Menyimpan...' : 'Offline / Error'}`}
          >
            {cloudSyncStatus === 'synced' && (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="flex items-center gap-1 text-[11px]">
                  <Cloud size={13} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Cloud Firebase</span>
                  {lastSyncTime && <span className="opacity-70 font-normal">({lastSyncTime})</span>}
                </span>
              </>
            )}
            {cloudSyncStatus === 'syncing' && (
              <>
                <RefreshCw size={12} className="animate-spin text-amber-600 dark:text-amber-400" />
                <span className="text-[11px]">Menyimpan Cloud...</span>
              </>
            )}
            {cloudSyncStatus === 'error' && (
              <>
                <CloudOff size={13} className="text-rose-600" />
                <span className="text-[11px]">Sync Offline</span>
              </>
            )}
          </div>

          {/* SINKRON SEKARANG BUTTON */}
          <button
            type="button"
            onClick={handleManualSync}
            disabled={isManualSyncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer disabled:opacity-50"
            title="Simpan dan sinkronkan data ke Cloud Firestore sekarang"
          >
            <RefreshCw size={13} className={isManualSyncing ? 'animate-spin text-amber-500' : ''} />
            <span>{isManualSyncing ? 'Menyinkronkan...' : 'Sinkron'}</span>
          </button>

          {classes && classes.length > 1 && (
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Pilih Pasukan Kelas:</span>
              <select
                value={currentClass}
                onChange={e => onSelectHomeroomClass && onSelectHomeroomClass(e.target.value)}
                className="bg-transparent font-black text-xs text-slate-800 dark:text-white outline-none cursor-pointer"
              >
                {classes.map(c => (
                  <option key={c} value={c} className="text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800">
                    Kelas {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* AUTO-DISTRIBUTE BUTTON */}
          <button
            type="button"
            onClick={() => setIsAutoDistributeModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 transition-all cursor-pointer shadow-xs"
            title="Otomatisasi membagi siswa kelas ke dalam Regu Putra dan Regu Putri"
          >
            <Sparkles size={14} className="text-amber-500" />
            <span>Bagi Regu Otomatis</span>
          </button>

          {/* EXCEL EXPORT */}
          <button
            type="button"
            onClick={handleExportExcel}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer shadow-xs"
          >
            <Download size={14} />
            <span>Ekspor Excel</span>
          </button>

          {/* PRINT BUTTON */}
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-black bg-amber-600 hover:bg-amber-500 text-white transition-all shadow-md active:scale-95 cursor-pointer"
          >
            <Printer size={14} />
            <span>Cetak Dokumen</span>
          </button>
        </div>
      </div>

      {/* HEADER BANNER */}
      <div className="bg-gradient-to-r from-amber-700 via-amber-800 to-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 opacity-10 text-white pointer-events-none">
          <Compass size={260} />
        </div>

        <div className="relative z-10 max-w-4xl">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-amber-400 text-slate-950 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
              <Compass size={14} /> Gerakan Pramuka
            </span>
            <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-amber-100">
              Gugus Depan: {scoutData.gugusDepan || '01.001 - 01.002'}
            </span>
            <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-bold text-amber-200">
              {scoutData.academicYear || '2025/2026'} - {scoutData.semester || 'Ganjil'}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">
            Administrasi Pasukan & Regu Pramuka
          </h2>
          <p className="text-xs sm:text-sm text-amber-100 max-w-2xl font-medium leading-relaxed">
            Kelola data Gugus Depan, Nama Pasukan, pembagian Regu Penggalang (Putra & Putri), serta penugasan jabatan kepemimpinan regu (Pratama, Pinru, Wapinru, dan Anggota) dengan Nomor Tanda Anggota (NTA) lengkap.
          </p>
        </div>
      </div>

      {/* GUGUS DEPAN & PASUKAN SETTINGS CARD */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-amber-500/30 dark:border-amber-600/30 shadow-sm relative">
        <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-amber-600 dark:text-amber-400" />
            <h3 className="font-black text-slate-800 dark:text-white text-base">
              Identitas Gugus Depan & Pasukan
            </h3>
          </div>
          <span className="text-[11px] font-bold text-slate-400">
            Perubahan otomatis tersimpan
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* NAMA GUGUS DEPAN */}
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
              Nama / No. Gugus Depan (Gudep)
            </label>
            <input
              type="text"
              value={scoutData.gugusDepan}
              onChange={e => setScoutData({ ...scoutData, gugusDepan: e.target.value })}
              onBlur={() => saveScoutData(scoutData)}
              placeholder="Contoh: 01.001 - 01.002 Pangkalan SMPN 1 Kawali"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* NAMA PASUKAN */}
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
              Nama Pasukan Penggalang
            </label>
            <input
              type="text"
              value={scoutData.namaPasukan}
              onChange={e => setScoutData({ ...scoutData, namaPasukan: e.target.value })}
              onBlur={() => saveScoutData(scoutData)}
              placeholder="Contoh: Pasukan Ki Hajar Dewantara"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* PEMBINA PUTRA */}
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
              Pembina Pramuka Putra
            </label>
            <input
              type="text"
              value={scoutData.pembinaPutra || ''}
              onChange={e => setScoutData({ ...scoutData, pembinaPutra: e.target.value })}
              onBlur={() => saveScoutData(scoutData)}
              placeholder="Nama Pembina Putra..."
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* PEMBINA PUTRI */}
          <div>
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
              Pembina Pramuka Putri
            </label>
            <input
              type="text"
              value={scoutData.pembinaPutri || ''}
              onChange={e => setScoutData({ ...scoutData, pembinaPutri: e.target.value })}
              onBlur={() => saveScoutData(scoutData)}
              placeholder="Nama Pembina Putri..."
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* STATS OVERVIEW CARDS (6-GRID STATS) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Regu</p>
          <p className="text-2xl font-black text-slate-800 dark:text-white mt-1">{stats.totalRegu}</p>
          <p className="text-[10px] text-amber-600 dark:text-amber-400 font-bold mt-0.5">{stats.totalPutraRegu} Putra • {stats.totalPutriRegu} Putri</p>
        </div>

        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Anggota</p>
          <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.totalMembers}</p>
          <p className="text-[10px] text-slate-400 font-bold mt-0.5">Siswa Terdaftar</p>
        </div>

        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <p className="text-[10px] font-black text-amber-500 uppercase tracking-wider flex items-center gap-1">
            <Crown size={12} /> Pratama
          </p>
          <p className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">{stats.totalPratama}</p>
          <p className="text-[10px] text-slate-400 font-bold mt-0.5">Pemimpin Utama</p>
        </div>

        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <p className="text-[10px] font-black text-emerald-500 uppercase tracking-wider flex items-center gap-1">
            <Shield size={12} /> Pinru
          </p>
          <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.totalPinru}</p>
          <p className="text-[10px] text-slate-400 font-bold mt-0.5">Pemimpin Regu</p>
        </div>

        <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-wider flex items-center gap-1">
            <Award size={12} /> Wapinru
          </p>
          <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1">{stats.totalWapinru}</p>
          <p className="text-[10px] text-slate-400 font-bold mt-0.5">Wakil Pemimpin</p>
        </div>

        <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-[10px] font-black text-amber-100 uppercase tracking-wider">Aksi Cepat</p>
            <p className="text-xs font-bold mt-1 text-white">Tambah Regu</p>
          </div>
          <button
            type="button"
            onClick={handleOpenAddRegu}
            className="mt-2 w-full py-1.5 bg-white text-slate-900 rounded-xl text-xs font-black hover:bg-amber-50 transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 shadow-sm"
          >
            <Plus size={14} /> Buat Regu
          </button>
        </div>
      </div>

      {/* FILTER & VIEW TOGGLE BAR */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
          {/* SEARCH INPUT */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Cari nama siswa, NTA, kelas, atau nama regu..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* CATEGORY FILTER */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {(['SEMUA', 'Putra', 'Putri'] as const).map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  categoryFilter === cat
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs font-black'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {cat === 'SEMUA' ? 'Semua Kategori' : `Regu ${cat}`}
              </button>
            ))}
          </div>

          {/* ROLE FILTER */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {(['SEMUA', 'Pratama', 'Pinru', 'Pratama & Pinru', 'Wapinru', 'Anggota'] as const).map(r => (
              <button
                key={r}
                type="button"
                onClick={() => setRoleFilter(r)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  roleFilter === r
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs font-black'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {r === 'Pratama & Pinru' ? '👑🛡️ Pratama & Pinru' : r}
              </button>
            ))}
          </div>
        </div>

        {/* VIEW MODE TOGGLE & ADD REGU */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs font-black'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
              title="Tampilan Kartu Regu"
            >
              <LayoutGrid size={15} />
              <span className="hidden sm:inline">Kartu Regu</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs font-black'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
              title="Tampilan Tabel Database Induk"
            >
              <TableIcon size={15} />
              <span className="hidden sm:inline">Tabel Database</span>
            </button>
          </div>

          <button
            type="button"
            onClick={handleOpenAddRegu}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-black flex items-center gap-1.5 transition-all shadow-md active:scale-95 cursor-pointer"
          >
            <FolderPlus size={16} /> <span className="hidden sm:inline">+ Tambah Regu Baru</span>
          </button>
        </div>
      </div>

      {/* VIEW 1: REGU CARDS SECTION (GRID) */}
      {viewMode === 'grid' && (
        <>
          {scoutData.reguList.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 p-12 rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 text-center">
              <Compass size={48} className="mx-auto text-amber-500 mb-3 opacity-60 animate-bounce" />
              <h4 className="text-base font-black text-slate-800 dark:text-white">Belum Ada Regu Pramuka</h4>
              <p className="text-xs text-slate-400 max-w-sm mx-auto mt-1 mb-4">
                Silakan buat regu pertama Anda atau gunakan fitur 'Bagi Regu Otomatis' untuk membagi siswa kelas {currentClass}.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleOpenAddRegu}
                  className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-black transition-all cursor-pointer shadow-md inline-flex items-center gap-2"
                >
                  <Plus size={16} /> Buat Regu Manual
                </button>
                <button
                  type="button"
                  onClick={() => setIsAutoDistributeModalOpen(true)}
                  className="px-5 py-2.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-black transition-all cursor-pointer inline-flex items-center gap-2"
                >
                  <Sparkles size={16} /> Bagi Regu Otomatis
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {scoutData.reguList
                .filter(r => categoryFilter === 'SEMUA' || r.category === categoryFilter)
                .map((regu, rIdx) => {
                  const isPutra = regu.category === 'Putra';
                  const pratama = regu.members.find(m => m.role === 'Pratama' || m.role === 'Pratama & Pinru');
                  const pinru = regu.members.find(m => m.role === 'Pinru' || m.role === 'Pratama & Pinru');
                  const wapinru = regu.members.find(m => m.role === 'Wapinru');
                  const isPratamaPinruSame = pratama && pinru && pratama.id === pinru.id;
                  const reguMembers = regu.members.filter(m => {
                    if (roleFilter !== 'SEMUA') {
                      if (roleFilter === 'Pratama' && (m.role === 'Pratama' || m.role === 'Pratama & Pinru')) return true;
                      if (roleFilter === 'Pinru' && (m.role === 'Pinru' || m.role === 'Pratama & Pinru')) return true;
                      if (m.role !== roleFilter) return false;
                    }
                    if (!searchQuery) return true;
                    const term = searchQuery.toLowerCase();
                    return (
                      m.name.toLowerCase().includes(term) ||
                      (m.nta && m.nta.toLowerCase().includes(term)) ||
                      m.class.toLowerCase().includes(term)
                    );
                  });

                  return (
                    <div
                      key={regu.id}
                      className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/90 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col justify-between"
                    >
                      {/* REGU HEADER */}
                      <div
                        className={`p-5 text-white flex items-center justify-between gap-3 ${
                          isPutra
                            ? 'bg-gradient-to-r from-amber-700 via-amber-800 to-slate-900'
                            : 'bg-gradient-to-r from-rose-700 via-pink-800 to-slate-900'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm shadow-md ${
                              isPutra ? 'bg-amber-600 border border-amber-400' : 'bg-pink-600 border border-pink-400'
                            }`}
                          >
                            {rIdx + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-black text-base tracking-tight">{regu.name}</h4>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                  isPutra ? 'bg-amber-400 text-slate-950' : 'bg-pink-300 text-slate-950'
                                }`}
                              >
                                Penggalang {regu.category}
                              </span>
                            </div>
                            {regu.motto && (
                              <p className="text-[11px] text-white/80 italic mt-0.5 font-serif">
                                "{regu.motto}"
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleOpenEditRegu(regu)}
                            className="p-2 hover:bg-white/20 rounded-xl text-white/90 transition-colors cursor-pointer"
                            title="Edit Info Regu"
                          >
                            <Edit3 size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRegu(regu.id, regu.name)}
                            className="p-2 hover:bg-red-500/40 rounded-xl text-red-200 hover:text-white transition-colors cursor-pointer"
                            title="Hapus Regu"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>

                      {/* QUICK LEADERSHIP BAR (PRATAMA, PINRU, WAPINRU) */}
                      <div className="px-5 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                        <div className="flex flex-wrap items-center gap-3">
                          {isPratamaPinruSame ? (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-amber-100/80 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-700">
                              <Crown size={13} className="text-amber-600" />
                              <Shield size={13} className="text-emerald-600" />
                              <span className="text-[11px] font-black text-amber-800 dark:text-amber-200">Pratama & Pinru:</span>
                              <strong className="text-slate-900 dark:text-white font-bold text-[11px] ml-0.5">
                                {pratama.name}
                              </strong>
                              <span className="text-[9px] font-bold text-amber-700 dark:text-amber-300 bg-white/60 dark:bg-slate-900/60 px-1.5 py-0.2 rounded-md">
                                Rangkap
                              </span>
                            </div>
                          ) : (
                            <>
                              {pratama && (
                                <div className="flex items-center gap-1">
                                  <Crown size={13} className="text-amber-500" />
                                  <span className="text-[11px] font-bold text-slate-400">Pratama:</span>
                                  <strong className="text-slate-800 dark:text-white font-bold text-[11px]">
                                    {pratama.name}
                                  </strong>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Shield size={13} className="text-emerald-500" />
                                <span className="text-[11px] font-bold text-slate-400">Pinru:</span>
                                <strong className="text-slate-800 dark:text-white font-bold text-[11px]">
                                  {pinru ? pinru.name : <span className="text-slate-400 font-normal italic">Belum ditunjuk</span>}
                                </strong>
                              </div>
                            </>
                          )}
                          <div className="flex items-center gap-1">
                            <Award size={13} className="text-indigo-500" />
                            <span className="text-[11px] font-bold text-slate-400">Wapinru:</span>
                            <strong className="text-slate-800 dark:text-white font-bold text-[11px]">
                              {wapinru ? wapinru.name : <span className="text-slate-400 font-normal italic">Belum ditunjuk</span>}
                            </strong>
                          </div>
                        </div>

                        <span className="text-[11px] font-bold text-slate-400">
                          {regu.members.length} Anggota
                        </span>
                      </div>

                      {/* MEMBERS TABLE */}
                      <div className="p-4 flex-1">
                        {regu.members.length === 0 ? (
                          <div className="py-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                            <Users size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                              Belum ada anggota di {regu.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => handleOpenAddMember(regu.id)}
                              className="mt-2 text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                            >
                              + Tambah Anggota Pertama
                            </button>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                  <th className="py-2 px-2 w-[8%] text-center">No</th>
                                  <th className="py-2 px-3 w-[36%]">Nama Siswa</th>
                                  <th className="py-2 px-2 w-[14%] text-center">Kelas</th>
                                  <th className="py-2 px-2 w-[20%]">NTA</th>
                                  <th className="py-2 px-2 w-[14%] text-center">Jabatan</th>
                                  <th className="py-2 px-2 w-[8%] text-center">Aksi</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                {reguMembers.map((member, mIdx) => {
                                  const isDual = member.role === 'Pratama & Pinru';
                                  const isPratama = member.role === 'Pratama';
                                  const isPinru = member.role === 'Pinru';
                                  const isWapinru = member.role === 'Wapinru';

                                  return (
                                    <tr
                                      key={member.id}
                                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors group"
                                    >
                                      <td className="py-2.5 px-2 text-center font-bold text-slate-400 text-[11px]">
                                        {mIdx + 1}
                                      </td>
                                      <td className="py-2.5 px-3">
                                        <div className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                                          {isDual && (
                                            <span className="flex items-center gap-0.5 shrink-0" title="Pratama & Pinru (Rangkap Jabatan)">
                                              <Crown size={12} className="text-amber-500" />
                                              <Shield size={12} className="text-emerald-500" />
                                            </span>
                                          )}
                                          {isPratama && <Crown size={12} className="text-amber-500 shrink-0" />}
                                          {isPinru && <Shield size={12} className="text-emerald-500 shrink-0" />}
                                          {isWapinru && <Award size={12} className="text-indigo-500 shrink-0" />}
                                          <span className="truncate">{member.name}</span>
                                        </div>
                                        {member.notes && (
                                          <p className="text-[10px] text-slate-400 truncate max-w-[180px]">
                                            {member.notes}
                                          </p>
                                        )}
                                      </td>
                                      <td className="py-2.5 px-2 text-center font-bold text-slate-600 dark:text-slate-300">
                                        <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-[10px]">
                                          {member.class}
                                        </span>
                                      </td>
                                      <td className="py-2.5 px-2 font-mono text-[11px] text-slate-600 dark:text-slate-400">
                                        {member.nta || <span className="text-slate-300">-</span>}
                                      </td>
                                      <td className="py-2.5 px-2 text-center">
                                        <button
                                          type="button"
                                          onClick={() => handleOpenEditMember(regu.id, member)}
                                          title="Klik untuk ubah jabatan"
                                          className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition-transform hover:scale-105 cursor-pointer border ${
                                            isDual
                                              ? 'bg-gradient-to-r from-amber-100 to-emerald-100 text-amber-900 border-amber-300 dark:from-amber-950/80 dark:to-emerald-950/80 dark:text-amber-200 dark:border-amber-700'
                                              : isPratama
                                              ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-700'
                                              : isPinru
                                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-700'
                                              : isWapinru
                                              ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950/80 dark:text-indigo-300 dark:border-indigo-700'
                                              : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                                          }`}
                                        >
                                          {member.role}
                                        </button>
                                      </td>
                                      <td className="py-2.5 px-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <button
                                            type="button"
                                            onClick={() => handleOpenEditMember(regu.id, member)}
                                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-amber-600 rounded-md transition-colors cursor-pointer"
                                            title="Edit Anggota"
                                          >
                                            <Edit3 size={13} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteMember(regu.id, member.id, member.name)}
                                            className="p-1 hover:bg-red-50 dark:hover:bg-red-950/40 text-slate-400 hover:text-red-500 rounded-md transition-colors cursor-pointer"
                                            title="Hapus Anggota"
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* REGU FOOTER ACTION */}
                      <div className="p-3 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                        <span className="text-[11px] font-bold text-slate-400 pl-2">
                          {regu.members.length} Anggota
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenAddMember(regu.id)}
                          className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-black transition-all flex items-center gap-1 shadow-xs active:scale-95 cursor-pointer"
                        >
                          <Plus size={14} /> Tambah Anggota
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* VIEW 2: DATABASE INDUK TABLE VIEW (SEJAJAR DENGAN DATABASE WALI KELAS) */}
      {viewMode === 'table' && (
        <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h4 className="font-black text-slate-800 dark:text-white text-base">
                Tabel Database Induk Anggota Pasukan ({filteredMembers.length} Siswa)
              </h4>
              <p className="text-xs text-slate-400 mt-0.5">
                Rekapitulasi seluruh anggota, nomor tanda anggota (NTA), regu pangkalan, dan pembagian tugas kepemimpinan.
              </p>
            </div>
            <span className="px-3 py-1 bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 rounded-xl text-xs font-black">
              Total {stats.totalMembers} Terdaftar
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/80 text-[10px] font-black text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <th className="py-3 px-4 w-[5%] text-center">No</th>
                  <th className="py-3 px-4 w-[25%]">Nama Lengkap Anggota</th>
                  <th className="py-3 px-3 w-[10%] text-center">Kelas</th>
                  <th className="py-3 px-4 w-[18%]">NTA (Nomor Anggota)</th>
                  <th className="py-3 px-4 w-[18%]">Regu & Kategori</th>
                  <th className="py-3 px-3 w-[12%] text-center">Jabatan</th>
                  <th className="py-3 px-3 w-[12%] text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400 font-bold">
                      Tidak ditemukan data anggota sesuai pencarian atau filter yang dipilih.
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member, idx) => {
                    const isDual = member.role === 'Pratama & Pinru';
                    const isPratama = member.role === 'Pratama';
                    const isPinru = member.role === 'Pinru';
                    const isWapinru = member.role === 'Wapinru';

                    return (
                      <tr key={`${member.reguId}-${member.id}`} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="py-3 px-4 text-center font-bold text-slate-400">{idx + 1}</td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            {isDual && (
                              <span className="flex items-center gap-0.5 shrink-0" title="Pratama & Pinru (Rangkap Jabatan)">
                                <Crown size={14} className="text-amber-500" />
                                <Shield size={14} className="text-emerald-500" />
                              </span>
                            )}
                            {isPratama && <Crown size={14} className="text-amber-500 shrink-0" />}
                            {isPinru && <Shield size={14} className="text-emerald-500 shrink-0" />}
                            {isWapinru && <Award size={14} className="text-indigo-500 shrink-0" />}
                            <span>{member.name}</span>
                          </div>
                          {member.notes && (
                            <p className="text-[10px] text-slate-400 mt-0.5">{member.notes}</p>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 font-bold text-[11px] text-slate-700 dark:text-slate-300">
                            {member.class}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px] text-slate-700 dark:text-slate-300">
                          {member.nta || <span className="text-slate-300">-</span>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-800 dark:text-slate-200">{member.reguName}</div>
                          <span className={`text-[10px] font-bold ${member.reguCategory === 'Putra' ? 'text-amber-600' : 'text-pink-600'}`}>
                            Penggalang {member.reguCategory}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            isDual
                              ? 'bg-gradient-to-r from-amber-100 to-emerald-100 text-amber-900 border border-amber-300 dark:from-amber-950/80 dark:to-emerald-950/80 dark:text-amber-200 dark:border-amber-700'
                              : isPratama
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300'
                              : isPinru
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300'
                              : isWapinru
                              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/80 dark:text-indigo-300'
                              : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                          }`}>
                            {member.role}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleOpenEditMember(member.reguId, member)}
                              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-amber-600 rounded-lg transition-colors cursor-pointer"
                              title="Edit Anggota"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteMember(member.reguId, member.id, member.name)}
                              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/40 text-slate-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                              title="Hapus Anggota"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: OTOMATISASI BAGI REGU DARI SISWA */}
      {isAutoDistributeModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsAutoDistributeModalOpen(false)}
          />
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 z-10">
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={22} />
                <h3 className="font-black text-lg">
                  Otomatisasi Pembagian Regu
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAutoDistributeModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                <p className="font-bold mb-1">Cara Kerja Otomatisasi:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Mengambil seluruh siswa di Kelas {currentClass} ({availableClassStudents.length} siswa).</li>
                  <li>Memisahkan siswa laki-laki ke <strong>Regu Putra</strong> dan siswa perempuan ke <strong>Regu Putri</strong>.</li>
                  <li>Menunjuk <strong>Pratama</strong>, <strong>Pinru</strong> (Pemimpin Regu), dan <strong>Wapinru</strong> secara terstruktur.</li>
                  <li>Men-generate Nomor Tanda Anggota (NTA) secara otomatis.</li>
                </ul>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Kapasitas Anggota per Regu (Standar: 6-8 siswa)
                </label>
                <div className="flex items-center gap-3">
                  {[6, 8, 10, 12].map(num => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setMembersPerRegu(num)}
                      className={`flex-1 py-3 rounded-2xl text-xs font-black border transition-all ${
                        membersPerRegu === num
                          ? 'bg-amber-500 text-white border-amber-600 shadow-md'
                          : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {num} Siswa
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsAutoDistributeModalOpen(false)}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 text-xs"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={handleExecuteAutoDistribute}
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 text-white font-black rounded-2xl text-xs uppercase tracking-wider shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Sparkles size={15} /> Jalankan Pembagian
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: TAMBAH / EDIT REGU */}
      {isReguModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsReguModalOpen(false)}
          />
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-3xl shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 z-10">
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Compass size={22} />
                <h3 className="font-black text-lg">
                  {editingRegu ? 'Edit Regu Pramuka' : 'Tambah Regu Baru'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsReguModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg text-white"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveRegu} className="p-6 space-y-4">
              {/* KATEGORI */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Kategori Penggalang
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setReguCategory('Putra');
                      if (!reguName) setReguName(PRESET_REGU_PUTRA[0]);
                    }}
                    className={`py-2.5 rounded-xl text-xs font-black transition-all border flex items-center justify-center gap-2 ${
                      reguCategory === 'Putra'
                        ? 'bg-amber-500 text-white border-amber-600 shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Penggalang Putra
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReguCategory('Putri');
                      if (!reguName) setReguName(PRESET_REGU_PUTRI[0]);
                    }}
                    className={`py-2.5 rounded-xl text-xs font-black transition-all border flex items-center justify-center gap-2 ${
                      reguCategory === 'Putri'
                        ? 'bg-pink-500 text-white border-pink-600 shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Penggalang Putri
                  </button>
                </div>
              </div>

              {/* NAMA REGU */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Nama Regu
                </label>
                <input
                  type="text"
                  required
                  value={reguName}
                  onChange={e => setReguName(e.target.value)}
                  placeholder={reguCategory === 'Putra' ? 'Contoh: Regu Garuda' : 'Contoh: Regu Melati'}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500"
                />

                {/* PRESETS BUTTONS */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(reguCategory === 'Putra' ? PRESET_REGU_PUTRA : PRESET_REGU_PUTRI).slice(0, 5).map(preset => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setReguName(preset)}
                      className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-amber-100 text-[10px] font-bold text-slate-600 dark:text-slate-300 transition-colors"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* MOTTO REGU */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Semboyan / Motto Regu (Opsional)
                </label>
                <input
                  type="text"
                  value={reguMotto}
                  onChange={e => setReguMotto(e.target.value)}
                  placeholder="Contoh: Tangkas, Cerdas, Berani"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500"
                />
              </div>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsReguModalOpen(false)}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 text-xs"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 text-white font-black rounded-2xl text-xs uppercase tracking-wider shadow-md active:scale-95 transition-all cursor-pointer"
                >
                  Simpan Regu
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: TAMBAH / EDIT ANGGOTA REGU */}
      {isMemberModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsMemberModalOpen(false)}
          />
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 z-10">
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={22} />
                <h3 className="font-black text-lg">
                  {editingMember ? 'Edit Anggota Regu' : 'Tambah Anggota Regu'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsMemberModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg text-white"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveMember} className="p-6 space-y-4">
              {/* JABATAN SELECTOR (PRATAMA / PINRU / WAPINRU / ANGGOTA / PRATAMA & PINRU) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Jabatan di Regu
                  </label>
                  <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">
                    *1 Siswa dapat menjabat Pratama & Pinru sekaligus
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(
                    [
                      { role: 'Pratama & Pinru', label: 'Pratama & Pinru', desc: 'Rangkap Jabatan', icon: 'dual' },
                      { role: 'Pratama', label: 'Pratama', desc: 'Pemimpin Utama', icon: 'pratama' },
                      { role: 'Pinru', label: 'Pinru', desc: 'Pemimpin Regu', icon: 'pinru' },
                      { role: 'Wapinru', label: 'Wapinru', desc: 'Wakil Pemimpin', icon: 'wapinru' },
                      { role: 'Anggota', label: 'Anggota', desc: 'Anggota Regu', icon: 'anggota' },
                    ] as const
                  ).map(item => {
                    const isSelected = memberRole === item.role;
                    return (
                      <button
                        key={item.role}
                        type="button"
                        onClick={() => setMemberRole(item.role as ScoutRole)}
                        className={`p-2.5 rounded-2xl text-xs font-black transition-all border flex flex-col items-center justify-center gap-1 cursor-pointer ${
                          isSelected
                            ? item.role === 'Pratama & Pinru'
                              ? 'bg-gradient-to-r from-amber-600 to-emerald-600 text-white border-amber-600 shadow-md scale-[1.02]'
                              : item.role === 'Pratama'
                              ? 'bg-amber-500 text-white border-amber-600 shadow-md scale-[1.02]'
                              : item.role === 'Pinru'
                              ? 'bg-emerald-600 text-white border-emerald-700 shadow-md scale-[1.02]'
                              : item.role === 'Wapinru'
                              ? 'bg-indigo-600 text-white border-indigo-700 shadow-md scale-[1.02]'
                              : 'bg-slate-700 text-white border-slate-800 shadow-md scale-[1.02]'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-amber-400'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          {item.icon === 'dual' && (
                            <>
                              <Crown size={14} className={isSelected ? 'text-amber-200' : 'text-amber-500'} />
                              <Shield size={14} className={isSelected ? 'text-emerald-200' : 'text-emerald-500'} />
                            </>
                          )}
                          {item.icon === 'pratama' && <Crown size={15} />}
                          {item.icon === 'pinru' && <Shield size={15} />}
                          {item.icon === 'wapinru' && <Award size={15} />}
                          {item.icon === 'anggota' && <Users size={15} />}
                        </div>
                        <span className="text-[11px] font-black">{item.label}</span>
                        <span className={`text-[9px] font-normal ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
                          {item.desc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* QUICK SELECT FROM STUDENTS LIST */}
              {availableClassStudents.length > 0 && !editingMember && (
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                    Pilih Cepat dari Siswa Kelas {currentClass}
                  </label>
                  <select
                    value={memberStudentId}
                    onChange={e => handleSelectStudentForMember(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500 cursor-pointer"
                  >
                    <option value="">-- Atau Ketik Nama Manual di Bawah --</option>
                    {availableClassStudents.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.class}) {s.nisn ? `- NISN: ${s.nisn}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* NAMA SISWA */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Nama Lengkap Siswa
                </label>
                <input
                  type="text"
                  required
                  value={memberName}
                  onChange={e => setMemberName(e.target.value)}
                  placeholder="Ketik nama lengkap siswa..."
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500"
                />
              </div>

              {/* KELAS & NTA */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                    Kelas
                  </label>
                  <input
                    type="text"
                    required
                    value={memberClass}
                    onChange={e => setMemberClass(e.target.value)}
                    placeholder="Contoh: 9A"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                    NTA (No. Tanda Anggota)
                  </label>
                  <input
                    type="text"
                    value={memberNta}
                    onChange={e => setMemberNta(e.target.value)}
                    placeholder="Contoh: 10.07.01.001"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-mono font-bold outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              {/* CATATAN / KEAHLIAN */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Catatan / Spesialisasi (Opsional)
                </label>
                <input
                  type="text"
                  value={memberNotes}
                  onChange={e => setMemberNotes(e.target.value)}
                  placeholder="Contoh: Pionering, Semaphore, Morse, P3K, Tali-temali"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold outline-none focus:border-amber-500"
                />
              </div>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsMemberModalOpen(false)}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 text-xs"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 text-white font-black rounded-2xl text-xs uppercase tracking-wider shadow-md active:scale-95 transition-all cursor-pointer"
                >
                  Simpan Anggota
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
